import * as Sentry from "@sentry/react";

const DEFAULT_BATCH_WINDOW_MS = 2000;

export interface SentryErrorBatcherOptions<TContext> {
  /** 마지막 report 이후 이 시간만큼 잠잠하면 flush. 기본 1000ms */
  windowMs?: number;
  /** 같은 키끼리 묶임. 기본은 `${error.name}:${error.message}` */
  getKey?: (error: unknown) => string;
  /** 묶인 context 배열을 Sentry `extra`로 변환. 기본은 `{ failedCount, contexts }` */
  buildExtra?: (contexts: TContext[]) => Record<string, unknown>;
}

export interface SentryErrorBatcher<TContext> {
  /** 에러를 버퍼에 추가하고 flush 타이머를 갱신 */
  report: (error: unknown, context: TContext) => void;
  /** 즉시 버퍼를 비우고 Sentry로 전송 */
  flush: () => void;
  /** flush 후 타이머 정리. 컴포넌트 unmount 시 호출 */
  destroy: () => void;
}

function defaultGetKey(error: unknown): string {
  if (error instanceof Error) return `${error.name}:${error.message}`;
  return String(error);
}

function defaultBuildExtra<TContext>(contexts: TContext[]): Record<string, unknown> {
  return { failedCount: contexts.length, contexts };
}

/**
 * 짧은 시간 윈도우 동안 발생한 에러를 같은 원인끼리 묶어서
 * 1번의 `Sentry.captureException`으로 전송하는 배치 리포터.
 *
 * @example
 * ```ts
 * const batcher = createSentryErrorBatcher<{ name: string }>({
 *   buildExtra: (files) => ({ failedCount: files.length, files }),
 * });
 *
 * batcher.report(err, { name: "a.jpg" });
 * batcher.report(err, { name: "b.jpg" }); // 같은 원인이면 묶여서 1번만 전송
 * ```
 */
export function createSentryErrorBatcher<TContext>(
  options: SentryErrorBatcherOptions<TContext> = {},
): SentryErrorBatcher<TContext> {
  const windowMs = options.windowMs ?? DEFAULT_BATCH_WINDOW_MS;
  const getKey = options.getKey ?? defaultGetKey;
  const buildExtra = options.buildExtra ?? defaultBuildExtra;

  const buffer = new Map<string, { error: unknown; contexts: TContext[] }>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (buffer.size === 0) return;

    for (const { error, contexts } of buffer.values()) {
      Sentry.captureException(error, { extra: buildExtra(contexts) });
    }
    buffer.clear();
  };

  const report = (error: unknown, context: TContext) => {
    const key = getKey(error);
    const existing = buffer.get(key);
    if (existing) {
      existing.contexts.push(context);
    } else {
      buffer.set(key, { error, contexts: [context] });
    }

    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(flush, windowMs);
  };

  const destroy = () => {
    flush();
  };

  return { report, flush, destroy };
}
